import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../config/api';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';
import { SETTINGS_TAB_PERMISSIONS } from '../config/permissions';
import { useTheme } from '../context/ThemeContext';
import { FiSettings, FiUser, FiDroplet, FiUsers, FiShield, FiSave, FiX, FiPlus, FiEdit, FiTrash2, FiMail, FiCalendar, FiEdit2, FiUserPlus, FiUserMinus, FiInfo, FiCode, FiDollarSign, FiLogIn, FiFileText, FiCamera } from 'react-icons/fi';
import EmailConfig from '../components/email/EmailConfig';
import CurrencySyncSettings from '../components/CurrencySyncSettings';
import SalesLetterheadSettings from '../components/SalesLetterheadSettings';
import OrgTaxProfileSettings from '../components/OrgTaxProfileSettings';
import OcrSettings from '../components/OcrSettings';
import ApiEndpointsSettings from '../components/ApiEndpointsSettings';
import RolesManagement from '../components/RolesManagement';
import RoleSelect from '../components/RoleSelect';
import MoreMenu from '../components/MoreMenu';
import { useAppRoles, roleLabel } from '../hooks/useAppRoles';
import LoadingButton from '../components/LoadingButton';
import ThemeToggle from '../components/ThemeToggle';
import './Settings.css';
import { formatLocalDate, formatRecordCreatedOn, formatRecordModifiedOn } from '../utils/formatTimestamp';
import { appAlert, appConfirm } from '../utils/appDialog';
import ListPager from '../components/ListPager';
import { useClientPager } from '../hooks/useClientPager';

const Settings = () => {
  const { user, refreshUser } = useAuth();
  const { hasPermission } = usePermissions();
  const { accentTheme, accentThemes, themeMode, changeAccentTheme, isDark } = useTheme();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(tabFromUrl || 'profile');
  const [profileData, setProfileData] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    password: '',
    confirmPassword: '',
  });

  // Fetch user details
  useQuery(
    ['user-details', user?.id],
    () => api.get(`/users/${user?.id}`).then(res => res.data),
    {
      enabled: !!user?.id,
      onSuccess: (data) => {
        setProfileData({
          first_name: data.first_name || '',
          last_name: data.last_name || '',
          email: data.email || '',
          phone: data.phone || '',
          password: '',
          confirmPassword: '',
        });
      },
    }
  );

  const updateProfileMutation = useMutation(
    async (data) => {
      // All users can update their own profile
      const updateData = {
        email: data.email,
        first_name: data.first_name,
        last_name: data.last_name,
        phone: data.phone,
      };
      
      // Only include role and is_active if user is admin
      if (user?.role === 'admin') {
        // Admin can update all fields, but for own profile we don't need role/is_active
        // They can manage other users from Users page
      }
      
      await api.put(`/users/${user.id}`, updateData);
      
      if (data.password) {
        await api.put(`/users/${user.id}/password`, { password: data.password });
      }
    },
    {
      onSuccess: async () => {
        queryClient.invalidateQueries(['user-details', user?.id]);
        queryClient.invalidateQueries('user');
        // Refresh user context
        await refreshUser();
        void appAlert('Profile updated successfully');
        setProfileData({ ...profileData, password: '', confirmPassword: '' });
      },
      onError: (error) => {
        void appAlert('Failed to update profile: ' + (error.response?.data?.error || error.message));
      },
    }
  );

  const handleProfileSubmit = (e) => {
    e.preventDefault();
    
    if (profileData.password && profileData.password !== profileData.confirmPassword) {
      void appAlert('Passwords do not match');
      return;
    }

    if (profileData.password && profileData.password.length < 6) {
      void appAlert('Password must be at least 6 characters');
      return;
    }

    updateProfileMutation.mutate(profileData);
  };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: FiUser },
    { id: 'theme', label: 'Theme', icon: FiDroplet },
    { id: 'endpoints', label: 'API endpoints', icon: FiCode },
    { id: 'users', label: 'Users', icon: FiUsers },
    { id: 'roles', label: 'Roles', icon: FiShield },
    { id: 'groups', label: 'Groups', icon: FiUsers },
    { id: 'permissions', label: 'Access control', icon: FiShield },
    { id: 'currency', label: 'Currency sync', icon: FiDollarSign },
    { id: 'sales-templates', label: 'Sales letterhead', icon: FiFileText },
    { id: 'org-tax', label: 'Org tax profile', icon: FiShield },
    { id: 'ocr', label: 'OCR', icon: FiCamera },
    { id: 'email', label: 'Email configuration', icon: FiMail },
  ].map((tab) => ({
    ...tab,
    permission: Object.prototype.hasOwnProperty.call(SETTINGS_TAB_PERMISSIONS, tab.id)
      ? SETTINGS_TAB_PERMISSIONS[tab.id]
      : tab.permission,
  }));

  const filteredTabs = tabs.filter((tab) => {
    if (!tab.permission) return true;
    if (hasPermission(tab.permission)) return true;
    // Sales letterhead is also available to service managers
    if (tab.id === 'sales-templates' && hasPermission('service_manage')) return true;
    if (tab.id === 'ocr' && hasPermission('ai_matching_manage')) return true;
    return false;
  });

  useEffect(() => {
    if (tabFromUrl) {
      setActiveTab(tabFromUrl === 'vendor-ocr' ? 'ocr' : tabFromUrl);
    }
  }, [tabFromUrl]);

  useEffect(() => {
    if (!filteredTabs.some((tab) => tab.id === activeTab)) {
      setActiveTab(filteredTabs[0]?.id || 'profile');
    }
  }, [filteredTabs, activeTab]);

  return (
    <div className="settings-page">
      <div className="settings-header">
        <h1>
          <span className="nav-glyph nav-glyph--blue" aria-hidden="true">
            <FiSettings />
          </span>
          Settings
        </h1>
      </div>

      <div className="settings-container">
        <div className="settings-sidebar">
          {filteredTabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                className={`settings-tab ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <span className={`nav-glyph nav-glyph--${tab.id}`} aria-hidden="true">
                  <Icon />
                </span>
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        <div className="settings-content">
          {activeTab === 'profile' && (
            <div className="settings-section">
              <h2>
                <FiUser /> Profile Settings
              </h2>
              <form onSubmit={handleProfileSubmit} className="settings-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>First Name *</label>
                    <input
                      type="text"
                      value={profileData.first_name}
                      onChange={(e) => setProfileData({ ...profileData, first_name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Last Name *</label>
                    <input
                      type="text"
                      value={profileData.last_name}
                      onChange={(e) => setProfileData({ ...profileData, last_name: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Email *</label>
                    <input
                      type="email"
                      value={profileData.email}
                      onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Phone</label>
                    <input
                      type="tel"
                      value={profileData.phone}
                      onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>New Password</label>
                    <input
                      type="password"
                      value={profileData.password}
                      onChange={(e) => setProfileData({ ...profileData, password: e.target.value })}
                      placeholder="Leave blank to keep current password"
                    />
                  </div>
                  <div className="form-group">
                    <label>Confirm Password</label>
                    <input
                      type="password"
                      value={profileData.confirmPassword}
                      onChange={(e) => setProfileData({ ...profileData, confirmPassword: e.target.value })}
                      placeholder="Confirm new password"
                    />
                  </div>
                </div>
                <div className="form-actions">
                  <LoadingButton
                    type="submit"
                    className="btn btn-primary"
                    icon={FiSave}
                    loading={updateProfileMutation.isLoading}
                    loadingLabel="Saving..."
                  >
                    Save Changes
                  </LoadingButton>
                </div>
              </form>
            </div>
          )}

          {activeTab === 'theme' && (
            <div className="settings-section">
              <h2>
                <FiDroplet /> Theme Settings
              </h2>
              <div className="theme-selector">
                <h3 className="theme-section-label">Appearance</h3>
                <p>Choose light, dark, or match your system preference.</p>
                <ThemeToggle />
                <h3 className="theme-section-label">Accent color</h3>
                <p>
                  {isDark
                    ? 'Accent colors apply in light mode. Switch to light to preview brand palettes.'
                    : 'Choose a brand accent for the light theme.'}
                </p>
                <div className={`theme-grid ${isDark ? 'theme-grid--disabled' : ''}`}>
                  {Object.entries(accentThemes).map(([key, theme]) => (
                    <button
                      key={key}
                      type="button"
                      className={`theme-card ${accentTheme === key ? 'active' : ''}`}
                      onClick={() => !isDark && changeAccentTheme(key)}
                      disabled={isDark}
                      aria-pressed={accentTheme === key}
                    >
                      <div
                        className="theme-preview"
                        style={{
                          background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.primaryLight || theme.primary} 55%, ${theme.accent || theme.primary} 100%)`,
                        }}
                      >
                        <div className="theme-preview-content">
                          <div
                            className="theme-preview-chip"
                            style={{ backgroundColor: theme.surface, color: theme.text }}
                          >
                            Preview
                          </div>
                        </div>
                      </div>
                      <div className="theme-info">
                        <h3>{theme.name}</h3>
                        {accentTheme === key && !isDark && (
                          <span className="theme-active-badge">Active</span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
                <p className="theme-mode-note">
                  Current mode: <strong>{themeMode === 'system' ? 'System' : themeMode}</strong>
                  {themeMode === 'system' && ' (follows OS preference)'}
                </p>
              </div>
            </div>
          )}

          {activeTab === 'endpoints' && hasPermission('view_all_data') && <ApiEndpointsSettings />}

          {activeTab === 'users' && hasPermission('tab_users') && <UsersManagement />}

          {activeTab === 'roles' && hasPermission('permission_manage') && <RolesManagement />}

          {activeTab === 'groups' && hasPermission('group_manage') && <GroupsManagement />}

          {activeTab === 'permissions' && hasPermission('permission_manage') && <PermissionsManagement />}

          {activeTab === 'currency' && hasPermission('tab_metadata') && <CurrencySyncSettings />}
          {activeTab === 'sales-templates' &&
            (hasPermission('service_sales_manage') || hasPermission('service_manage')) && (
              <SalesLetterheadSettings />
            )}
          {activeTab === 'org-tax' && hasPermission('service_finance_manage') && (
            <OrgTaxProfileSettings />
          )}
          {activeTab === 'ocr' &&
            (hasPermission('erp_bills_manage') || hasPermission('ai_matching_manage')) && (
              <OcrSettings />
            )}
          {activeTab === 'email' && hasPermission('email_manage') && <EmailConfig />}
        </div>
      </div>
    </div>
  );
};

// Users Management Component (embedded in Settings)
const UsersManagement = () => {
  const { user: currentUser, impersonate, isImpersonating } = useAuth();
  const { data: appRoles = [] } = useAppRoles();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [impersonatingUserId, setImpersonatingUserId] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    role: 'candidate',
    phone: '',
    is_active: true,
  });

  const { data: users, isLoading } = useQuery(
    'users',
    () => api.get('/users').then(res => res.data)
  );

  const {
    pageItems: pagedUsers,
    pagination: settingsUsersPagination,
    setPage: setSettingsUsersPage,
  } = useClientPager(users || []);

  const { data: userDetails, isLoading: loadingDetails } = useQuery(
    ['user-details', selectedUser?.id],
    () => api.get(`/users/${selectedUser.id}`).then(res => res.data),
    {
      enabled: !!selectedUser && showUserDetails,
    }
  );

  const createMutation = useMutation(
    (data) => api.post('/users', data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('users');
        setShowModal(false);
        setFormData({
          email: '',
          password: '',
          first_name: '',
          last_name: '',
          role: 'candidate',
          phone: '',
          is_active: true,
        });
      },
      onError: (err) => {
        const msg = err.response?.data?.error || err.message;
        void appAlert(msg || 'Could not create user');
      },
    }
  );

  const updateMutation = useMutation(
    ({ id, data }) => api.put(`/users/${id}`, data),
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries('users');
        queryClient.invalidateQueries(['user-details', variables.id]);
        setShowModal(false);
        setEditingUser(null);
        setIsEditing(false);
        setFormData({
          email: '',
          password: '',
          first_name: '',
          last_name: '',
          role: 'candidate',
          phone: '',
          is_active: true,
        });
      },
    }
  );

  const updatePasswordMutation = useMutation(
    ({ id, password }) => api.put(`/users/${id}/password`, { password }),
    {
      onSuccess: (data, variables) => {
        setFormData({ ...formData, password: '' });
        queryClient.invalidateQueries(['user-details', variables.id]);
      },
    }
  );

  const deleteMutation = useMutation(
    (id) => api.delete(`/users/${id}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('users');
        setShowUserDetails(false);
        setSelectedUser(null);
        setIsEditing(false);
      },
      onError: (err) => {
        void appAlert(err.response?.data?.error || err.message || 'Failed to delete user');
      },
    }
  );

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    if (selectedUser.id === currentUser?.id) {
      void appAlert('Cannot delete your own account');
      return;
    }
    if (await appConfirm(`Are you sure you want to delete ${selectedUser.first_name} ${selectedUser.last_name}?`)) {
      deleteMutation.mutate(selectedUser.id);
    }
  };

  const canLoginAsUser = (target) =>
    currentUser?.role === 'admin' &&
    !isImpersonating &&
    target &&
    target.id !== currentUser?.id &&
    target.role !== 'admin';

  const handleLoginAs = async (target) => {
    if (!canLoginAsUser(target)) return;
    const name = [target.first_name, target.last_name].filter(Boolean).join(' ').trim() || target.email;
    if (!await appConfirm(`Login as ${name}? You will see the app as this user until you return to admin.`)) {
      return;
    }
    setImpersonatingUserId(target.id);
    try {
      await impersonate(target.id);
      queryClient.clear();
      setShowUserDetails(false);
      setSelectedUser(null);
      navigate('/');
    } catch (err) {
      console.error('Impersonation failed:', err);
      void appAlert(err?.response?.data?.error || 'Failed to login as user');
    } finally {
      setImpersonatingUserId(null);
    }
  };

  const handleUserClick = (userRow) => {
    const roleMeta = appRoles.find((r) => r.slug === userRow.role);
    if (roleMeta?.grants_candidate_profile || userRow.role === 'candidate') {
      navigate(`/candidates/${userRow.id}`);
      return;
    }
    setSelectedUser(userRow);
    setShowUserDetails(true);
    setIsEditing(false);
  };

  const handleEditUser = () => {
    if (userDetails) {
      setFormData({
        email: userDetails.email,
        first_name: userDetails.first_name,
        last_name: userDetails.last_name,
        role: userDetails.role,
        phone: userDetails.phone || '',
        is_active: userDetails.is_active,
        password: '',
      });
      setIsEditing(true);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setFormData({
      email: '',
      password: '',
      first_name: '',
      last_name: '',
      role: 'candidate',
      phone: '',
      is_active: true,
    });
  };

  const handleSaveUser = (e) => {
    e.preventDefault();
    if (!selectedUser) return;
    
    const submitData = { ...formData };
    const { password, ...userData } = submitData;
    
    updateMutation.mutate({ id: selectedUser.id, data: userData });
    
    if (password && password.trim() !== '') {
      updatePasswordMutation.mutate({ id: selectedUser.id, password });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const submitData = { ...formData };
    if (editingUser) {
      if (!submitData.password || String(submitData.password).trim() === '') {
        delete submitData.password;
      }
      updateMutation.mutate({ id: editingUser.id, data: submitData });
    } else {
      createMutation.mutate(submitData);
    }
  };

  if (isLoading) {
    return <div className="loading">Loading users...</div>;
  }

  return (
    <div className="settings-section">
      <div className="section-header-with-action">
        <h2>
          <FiUsers /> User Management
        </h2>
        <button className="btn btn-primary" onClick={() => {
          setEditingUser(null);
          setFormData({
            email: '',
            password: '',
            first_name: '',
            last_name: '',
            role: 'candidate',
            phone: '',
            is_active: true,
          });
          setShowModal(true);
        }}>
          <FiPlus /> Add User
        </button>
      </div>

      <div className="users-table-container">
        <table className="table users-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Phone</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {users && users.length > 0 ? (
              pagedUsers.map((user) => (
                <tr 
                  key={user.id} 
                  className="user-row"
                  onClick={() => handleUserClick(user)}
                >
                  <td>
                    <div className="user-name-cell">
                      <FiUser className="user-icon" />
                      <span>{user.first_name} {user.last_name}</span>
                      {canLoginAsUser(user) && (
                        <button
                          type="button"
                          className="btn btn-sm btn-secondary"
                          title="Login as this user"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleLoginAs(user);
                          }}
                          disabled={impersonatingUserId === user.id}
                        >
                          <FiLogIn />
                          {impersonatingUserId === user.id ? 'Logging in…' : 'Login as'}
                        </button>
                      )}
                    </div>
                  </td>
                  <td>{user.email}</td>
                  <td><span className="badge badge-info">{roleLabel(user.role, appRoles)}</span></td>
                  <td>{user.phone || 'N/A'}</td>
                  <td>
                    <span className={`badge badge-${user.is_active ? 'success' : 'danger'}`}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="empty-state">No users found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <ListPager
        page={settingsUsersPagination.page}
        pageCount={settingsUsersPagination.pageCount}
        total={settingsUsersPagination.total}
        limit={settingsUsersPagination.limit}
        onPageChange={setSettingsUsersPage}
      />

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{editingUser ? 'Edit User' : 'Add User'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>First Name</label>
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Last Name</label>
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>
              {(!editingUser || formData.password) && (
                <div className="form-group">
                  <label>Password{editingUser && ' (leave blank to keep current)'}</label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required={!editingUser}
                  />
                </div>
              )}
              <div className="form-group">
                <label>Role</label>
                <RoleSelect
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                  Active
                </label>
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingUser ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showUserDetails && selectedUser && (
        <div className="modal-overlay" onClick={() => {
          setShowUserDetails(false);
          setSelectedUser(null);
          setIsEditing(false);
        }}>
          <div className="modal-content user-details-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <FiUser /> User Details
              </h2>
              <button
                className="btn-close-modal"
                onClick={() => {
                  setShowUserDetails(false);
                  setSelectedUser(null);
                  setIsEditing(false);
                }}
              >
                <FiX />
              </button>
            </div>

            {loadingDetails ? (
              <div className="loading">Loading user details...</div>
            ) : userDetails ? (
              <>
                {!isEditing ? (
                  <div className="user-details-view">
                    <div className="user-profile-header">
                      <div className="user-avatar">
                        <FiUser size={48} />
                      </div>
                      <div className="user-basic-info">
                        <h3>{userDetails.first_name} {userDetails.last_name}</h3>
                        <p className="user-email">
                          <FiMail /> {userDetails.email}
                        </p>
                        <div className="user-status-badges">
                          <span className={`badge badge-${userDetails.is_active ? 'success' : 'danger'}`}>
                            {userDetails.is_active ? 'Active' : 'Inactive'}
                          </span>
                          <span className="badge badge-info">{userDetails.role}</span>
                        </div>
                      </div>
                    </div>

                    <div className="user-details-sections">
                      <div className="detail-section">
                        <h4>
                          <FiUser /> Personal Information
                        </h4>
                        <div className="detail-grid">
                          <div className="detail-item">
                            <strong>First Name:</strong>
                            <span>{userDetails.first_name}</span>
                          </div>
                          <div className="detail-item">
                            <strong>Last Name:</strong>
                            <span>{userDetails.last_name}</span>
                          </div>
                          <div className="detail-item">
                            <strong>Email:</strong>
                            <span>{userDetails.email}</span>
                          </div>
                          <div className="detail-item">
                            <strong>Phone:</strong>
                            <span>{userDetails.phone || 'N/A'}</span>
                          </div>
                          <div className="detail-item">
                            <strong>Role:</strong>
                            <span className="badge badge-info">{userDetails.role}</span>
                          </div>
                          <div className="detail-item">
                            <strong>Status:</strong>
                            <span className={`badge badge-${userDetails.is_active ? 'success' : 'danger'}`}>
                              {userDetails.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="detail-section">
                        <h4>
                          <FiCalendar /> Account Information
                        </h4>
                        <div className="detail-grid">
                          <div className="detail-item">
                            <strong>Created:</strong>
                            <span>{formatRecordCreatedOn(userDetails)}</span>
                          </div>
                          {(userDetails.modifiedon_timestamp || userDetails.updated_at) && (
                            <div className="detail-item">
                              <strong>Last Updated:</strong>
                              <span>{formatRecordModifiedOn(userDetails)}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {userDetails.groups && userDetails.groups.length > 0 && (
                        <div className="detail-section">
                          <h4>
                            <FiUsers /> Groups ({userDetails.groups.length})
                          </h4>
                          <div className="groups-list-inline">
                            {userDetails.groups.map((group) => (
                              <span key={group.id} className="group-badge">
                                {group.name}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="modal-actions user-detail-modal-actions">
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleEditUser}
                      >
                        <FiEdit2 /> Edit User
                      </button>
                      {canLoginAsUser(selectedUser) && (
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => handleLoginAs(selectedUser)}
                          disabled={impersonatingUserId === selectedUser.id}
                          title="Login as this user"
                        >
                          <FiLogIn />
                          {impersonatingUserId === selectedUser.id ? 'Logging in…' : 'Login as'}
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => {
                          setShowUserDetails(false);
                          setSelectedUser(null);
                        }}
                      >
                        Close
                      </button>
                      {selectedUser.id !== currentUser?.id && (
                        <button
                          type="button"
                          className="btn btn-danger user-detail-delete-btn"
                          onClick={handleDeleteUser}
                          disabled={deleteMutation.isLoading}
                          title="Delete user"
                        >
                          <FiTrash2 /> Delete
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <form onSubmit={handleSaveUser} className="user-edit-form">
                    <div className="user-profile-header">
                      <div className="user-avatar">
                        <FiUser size={48} />
                      </div>
                      <div className="user-basic-info">
                        <h3>Edit User Profile</h3>
                        <p className="user-email">{userDetails.email}</p>
                      </div>
                    </div>

                    <div className="form-sections">
                      <div className="form-section">
                        <h4>Personal Information</h4>
                        <div className="form-row">
                          <div className="form-group">
                            <label>First Name</label>
                            <input
                              type="text"
                              value={formData.first_name}
                              onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                              required
                            />
                          </div>
                          <div className="form-group">
                            <label>Last Name</label>
                            <input
                              type="text"
                              value={formData.last_name}
                              onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                              required
                            />
                          </div>
                        </div>
                        <div className="form-group">
                          <label>Email</label>
                          <input
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label>Phone</label>
                          <input
                            type="tel"
                            value={formData.phone}
                            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          />
                        </div>
                      </div>

                      <div className="form-section">
                        <h4>Account Settings</h4>
                        <div className="form-row">
                          <div className="form-group">
                            <label>Role</label>
                            <RoleSelect
                              value={formData.role}
                              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                              required
                            />
                          </div>
                          <div className="form-group">
                            <label>Status</label>
                            <select
                              value={formData.is_active ? 'active' : 'inactive'}
                              onChange={(e) => setFormData({ ...formData, is_active: e.target.value === 'active' })}
                            >
                              <option value="active">Active</option>
                              <option value="inactive">Inactive</option>
                            </select>
                          </div>
                        </div>
                        <div className="form-group">
                          <label>New Password (leave blank to keep current)</label>
                          <input
                            type="password"
                            value={formData.password}
                            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                            placeholder="Enter new password"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="modal-actions">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={handleCancelEdit}
                      >
                        Cancel
                      </button>
                      <LoadingButton
                        type="submit"
                        className="btn btn-primary"
                        icon={FiSave}
                        loading={updateMutation.isLoading}
                        loadingLabel="Saving..."
                      >
                        Save Changes
                      </LoadingButton>
                    </div>
                  </form>
                )}
              </>
            ) : (
              <div className="error">Failed to load user details</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Groups Management Component (embedded in Settings) - Simplified version
const GroupsManagement = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showGroupDetails, setShowGroupDetails] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '' });

  const { data: groups, isLoading } = useQuery(
    'groups',
    () => api.get('/groups').then(res => res.data)
  );

  const { data: users } = useQuery(
    'users',
    () => api.get('/users').then(res => res.data)
  );

  const { data: groupDetails, isLoading: loadingDetails } = useQuery(
    ['group-details', selectedGroup?.id],
    () => api.get(`/groups/${selectedGroup.id}`).then(res => res.data),
    {
      enabled: !!selectedGroup && showGroupDetails,
    }
  );

  const createMutation = useMutation(
    (data) => api.post('/groups', data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('groups');
        setShowModal(false);
        setFormData({ name: '', description: '' });
      },
    }
  );

  const updateMutation = useMutation(
    ({ id, data }) => api.put(`/groups/${id}`, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('groups');
        setShowModal(false);
        setFormData({ name: '', description: '' });
      },
    }
  );

  const deleteMutation = useMutation(
    (id) => api.delete(`/groups/${id}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('groups');
      },
    }
  );

  const addUserMutation = useMutation(
    ({ groupId, userId }) => api.post(`/groups/${groupId}/users/${userId}`),
    {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries('groups');
        queryClient.invalidateQueries(['group-details', variables.groupId]);
        queryClient.invalidateQueries({ queryKey: ['permissions', 'me'] });
        setShowUserModal(false);
      },
    }
  );

  const removeUserMutation = useMutation(
    ({ groupId, userId }) => api.delete(`/groups/${groupId}/users/${userId}`),
    {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries('groups');
        queryClient.invalidateQueries(['group-details', variables.groupId]);
        queryClient.invalidateQueries({ queryKey: ['permissions', 'me'] });
      },
    }
  );

  const handleViewGroup = (group) => {
    setSelectedGroup(group);
    setShowGroupDetails(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (selectedGroup) {
      updateMutation.mutate({ id: selectedGroup.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  if (isLoading) {
    return <div className="loading">Loading groups...</div>;
  }

  return (
    <div className="settings-section">
      <div className="section-header-with-action">
        <h2>
          <FiUsers /> Group Management
        </h2>
        <button className="btn btn-primary" onClick={() => {
          setSelectedGroup(null);
          setFormData({ name: '', description: '' });
          setShowModal(true);
        }}>
          <FiPlus /> Add Group
        </button>
      </div>

      <div className="groups-list">
        {groups && groups.length > 0 ? (
          groups.map((group) => (
            <div key={group.id} className="group-card" onClick={() => handleViewGroup(group)}>
              <div className="group-header">
                <div className="group-info">
                  <div className="group-title-section">
                    <h3>{group.name}</h3>
                    <button
                      className="btn-view-details"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewGroup(group);
                      }}
                      title="View group details"
                    >
                      <FiInfo /> View Details
                    </button>
                  </div>
                  {group.description && <p className="group-description">{group.description}</p>}
                  <div className="group-meta-info">
                    <span className="group-meta">
                      <FiUsers /> {group.user_count || 0} {group.user_count === 1 ? 'Member' : 'Members'}
                    </span>
                    {(group.createdon_timestamp || group.created_at) && (
                      <span className="group-date">
                        <FiCalendar /> Created {formatRecordCreatedOn(group, { year: 'numeric', month: 'short', day: 'numeric' })}
                      </span>
                    )}
                  </div>
                </div>
                <div className="group-actions" onClick={(e) => e.stopPropagation()}>
                  <MoreMenu
                    label={`Actions for ${group.name}`}
                    items={[
                      {
                        id: 'permissions',
                        label: 'Grant access',
                        icon: FiShield,
                        onSelect: () =>
                          navigate(`/settings?tab=permissions&groupId=${group.id}`),
                      },
                      {
                        id: 'edit',
                        label: 'Edit',
                        icon: FiEdit,
                        onSelect: () => {
                          setSelectedGroup(group);
                          setFormData({ name: group.name, description: group.description || '' });
                          setShowModal(true);
                        },
                      },
                      {
                        id: 'manage-users',
                        label: 'Manage Users',
                        icon: FiUserPlus,
                        onSelect: () => {
                          setSelectedGroup(group);
                          setShowUserModal(true);
                        },
                      },
                      {
                        id: 'delete',
                        label: 'Delete',
                        icon: FiTrash2,
                        danger: true,
                        onSelect: () => deleteMutation.mutate(group.id),
                      },
                    ]}
                  />
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="empty-state">No groups found</div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{selectedGroup ? 'Edit Group' : 'Add Group'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {selectedGroup ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showUserModal && selectedGroup && (
        <div className="modal-overlay" onClick={() => setShowUserModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Manage Users - {selectedGroup.name}</h2>
            <div className="users-list-modal">
              {users && users.length > 0 ? (
                users.map((user) => (
                  <div key={user.id} className="user-item-modal">
                    <span>{user.first_name} {user.last_name} ({user.email})</span>
                    <button
                      onClick={() => addUserMutation.mutate({ groupId: selectedGroup.id, userId: user.id })}
                      className="btn btn-success btn-sm"
                    >
                      <FiUserPlus /> Add
                    </button>
                  </div>
                ))
              ) : (
                <p>No users available</p>
              )}
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowUserModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showGroupDetails && selectedGroup && (
        <div className="modal-overlay" onClick={() => {
          setShowGroupDetails(false);
          setSelectedGroup(null);
        }}>
          <div className="modal-content group-details-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <FiInfo /> {selectedGroup.name}
              </h2>
              <button
                className="btn-close-modal"
                onClick={() => {
                  setShowGroupDetails(false);
                  setSelectedGroup(null);
                }}
              >
                <FiX />
              </button>
            </div>

            {loadingDetails ? (
              <div className="loading">Loading group details...</div>
            ) : groupDetails ? (
              <>
                <div className="group-details-info">
                  <div className="detail-section">
                    <h3>Group Information</h3>
                    {groupDetails.description && (
                      <p className="detail-description">{groupDetails.description}</p>
                    )}
                    <div className="detail-meta">
                      <div className="detail-item">
                        <strong>Created:</strong> {formatRecordCreatedOn(groupDetails)}
                      </div>
                      {(groupDetails.modifiedon_timestamp || groupDetails.updated_at) && (
                        <div className="detail-item">
                          <strong>Last Updated:</strong> {formatRecordModifiedOn(groupDetails)}
                        </div>
                      )}
                      <div className="detail-item">
                        <strong>Total Members:</strong> {groupDetails.users?.length || 0}
                      </div>
                    </div>
                  </div>

                  <div className="detail-section">
                    <div className="section-header">
                      <h3>
                        <FiUsers /> Group Members ({groupDetails.users?.length || 0})
                      </h3>
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => {
                          setShowUserModal(true);
                          setShowGroupDetails(false);
                        }}
                      >
                        <FiUserPlus /> Add Members
                      </button>
                    </div>

                    {groupDetails.users && groupDetails.users.length > 0 ? (
                      <div className="members-list">
                        {groupDetails.users.map((user) => (
                          <div key={user.id} className="member-item">
                            <div className="member-info">
                              <div className="member-name">
                                {user.first_name} {user.last_name}
                              </div>
                              <div className="member-email">{user.email}</div>
                              <div className="member-role">
                                <span className={`badge badge-info`}>{user.role}</span>
                              </div>
                              {user.assigned_at && (
                                <div className="member-date">
                                  Joined: {formatLocalDate(user.assigned_at)}
                                </div>
                              )}
                            </div>
                            <button
                              className="btn btn-danger btn-sm"
                              onClick={async () => {
                                if (await appConfirm(`Remove ${user.first_name} ${user.last_name} from this group?`)) {
                                  removeUserMutation.mutate({
                                    groupId: selectedGroup.id,
                                    userId: user.id,
                                  });
                                }
                              }}
                            >
                              <FiUserMinus /> Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="empty-members">
                        <p>No members in this group yet.</p>
                        <button
                          className="btn btn-primary"
                          onClick={() => {
                            setShowUserModal(true);
                            setShowGroupDetails(false);
                          }}
                        >
                          <FiUserPlus /> Add Members
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="modal-actions">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setSelectedGroup(groupDetails);
                      setFormData({ name: groupDetails.name, description: groupDetails.description || '' });
                      setShowModal(true);
                      setShowGroupDetails(false);
                    }}
                  >
                    <FiEdit /> Edit Group
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowGroupDetails(false);
                      setSelectedGroup(null);
                    }}
                  >
                    Close
                  </button>
                </div>
              </>
            ) : (
              <div className="error">Failed to load group details</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const PERMISSION_RESOURCE_LABELS = {
  tab: 'Navigation — tabs & sections (visibility)',
  job: 'Jobs — listings and postings',
  user: 'User accounts',
  candidate: 'Candidate profiles',
  timesheet: 'Timesheets',
  group: 'Groups',
  kpi: 'KPIs',
  service: 'Services — packages, enrollments, finance',
  requisition: 'Requisitions — work orders, scope, commercial terms, approvals',
  order: 'Client POs',
  board: 'Board',
  booking: 'Booking & calendar',
  customer: 'Customers',
  system: 'System',
};

const PERMISSION_RESOURCE_ORDER = [
  'tab',
  'job',
  'candidate',
  'user',
  'timesheet',
  'service',
  'customer',
  'requisition',
  'order',
  'board',
  'booking',
  'group',
  'kpi',
  'system',
];

const PERMISSION_ACTION_ORDER = ['view', 'create', 'edit', 'delete', 'assign', 'approve', 'manage', 'share'];

const ACTION_DISPLAY = {
  view: 'View / access',
  create: 'Create',
  edit: 'Edit / modify',
  delete: 'Delete',
  assign: 'Assign',
  approve: 'Approve',
  manage: 'Manage',
  share: 'Share',
};

const EMPTY_ACCESS_TYPE = {
  name: '',
  description: '',
  resource_type: 'tab',
  action: 'view',
  grant_admin: true,
};

// Permissions Management — group-first access; admins manage access types (catalog)
const PermissionsManagement = () => {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const { data: appRoles = [] } = useAppRoles({ includeAll: true });
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedRole, setSelectedRole] = useState('');
  const [permissionType, setPermissionType] = useState('group');
  const [accessTypeForm, setAccessTypeForm] = useState(EMPTY_ACCESS_TYPE);
  const [editingAccessType, setEditingAccessType] = useState(null);

  const {
    data: permissions,
    isLoading: loadingPermissions,
    isError: permissionsQueryError,
    error: permissionsQueryErr,
  } = useQuery('permissions', () => api.get('/permissions').then((res) => res.data));

  const { data: groups } = useQuery('groups', () => api.get('/groups').then((res) => res.data));

  useEffect(() => {
    const groupId = searchParams.get('groupId');
    if (!groupId || !groups?.length) return;
    const group = groups.find((g) => String(g.id) === String(groupId));
    if (group) {
      setPermissionType('group');
      setSelectedGroup(group);
      setSelectedRole('');
    }
  }, [searchParams, groups]);

  const {
    data: groupPermsData,
    isLoading: loadingGroupPerms,
    isError: groupPermsError,
  } = useQuery(
    ['group-permissions', selectedGroup?.id],
    () => api.get(`/permissions/group/${selectedGroup.id}`).then((res) => res.data),
    { enabled: !!selectedGroup && permissionType === 'group' }
  );

  const { data: rolePermsData, isLoading: loadingRolePerms } = useQuery(
    ['role-permissions', selectedRole],
    () => api.get(`/permissions/role/${selectedRole}`).then((res) => res.data),
    { enabled: !!selectedRole && permissionType === 'role' }
  );

  const updateGroupPermissionsMutation = useMutation(
    ({ groupId, permissions: permPayload }) =>
      api.post(`/permissions/group/${groupId}`, { permissions: permPayload }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['group-permissions', selectedGroup?.id]);
        // Members (incl. current session) must re-resolve nav grants immediately
        queryClient.invalidateQueries({ queryKey: ['permissions', 'me'] });
        void appAlert('Group access updated successfully. Members may need a page refresh if already signed in.');
      },
      onError: (e) => void appAlert(e.response?.data?.error || e.message),
    }
  );

  const updateRolePermissionsMutation = useMutation(
    ({ role, permissions: permPayload }) =>
      api.post(`/permissions/role/${role}`, { permissions: permPayload }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['role-permissions']);
        queryClient.invalidateQueries({ queryKey: ['permissions', 'me'] });
        void appAlert('Role default permissions updated successfully');
      },
      onError: (e) => void appAlert(e.response?.data?.error || e.message),
    }
  );

  const createAccessTypeMutation = useMutation(
    (payload) => api.post('/permissions', payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('permissions');
        queryClient.invalidateQueries(['role-permissions']);
        setAccessTypeForm(EMPTY_ACCESS_TYPE);
        void appAlert('Access type created');
      },
      onError: (e) => void appAlert(e.response?.data?.error || e.message),
    }
  );

  const updateAccessTypeMutation = useMutation(
    ({ id, ...payload }) => api.put(`/permissions/${id}`, payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('permissions');
        setEditingAccessType(null);
        void appAlert('Access type updated');
      },
      onError: (e) => void appAlert(e.response?.data?.error || e.message),
    }
  );

  const deleteAccessTypeMutation = useMutation((id) => api.delete(`/permissions/${id}`), {
    onSuccess: () => {
      queryClient.invalidateQueries('permissions');
      queryClient.invalidateQueries(['group-permissions']);
      queryClient.invalidateQueries(['role-permissions']);
      void appAlert('Access type deleted');
    },
    onError: (e) => void appAlert(e.response?.data?.error || e.message),
  });

  const permissionsByResource = useMemo(() => {
    const acc = {};
    (permissions || []).forEach((perm) => {
      const name = String(perm?.name || '');
      const resource = String(perm?.resource_type || '');
      if (
        resource === 'support' ||
        name === 'tab_support' ||
        name.startsWith('support_')
      ) {
        return;
      }
      if (!acc[perm.resource_type]) acc[perm.resource_type] = [];
      acc[perm.resource_type].push(perm);
    });
    Object.keys(acc).forEach((key) => {
      acc[key].sort((a, b) => {
        const ia = PERMISSION_ACTION_ORDER.indexOf(a.action);
        const ib = PERMISSION_ACTION_ORDER.indexOf(b.action);
        if (ia !== -1 || ib !== -1) {
          if (ia === -1) return 1;
          if (ib === -1) return -1;
          if (ia !== ib) return ia - ib;
        }
        return String(a.name).localeCompare(String(b.name));
      });
    });
    return acc;
  }, [permissions]);

  const sortedResourceEntries = useMemo(() => {
    return Object.entries(permissionsByResource).sort(([a], [b]) => {
      const ia = PERMISSION_RESOURCE_ORDER.indexOf(a);
      const ib = PERMISSION_RESOURCE_ORDER.indexOf(b);
      if (ia === -1 && ib === -1) return a.localeCompare(b);
      if (ia === -1) return 1;
      if (ib === -1) return -1;
      return ia - ib;
    });
  }, [permissionsByResource]);

  const getEffectivePermissions = () => {
    if (!permissions?.length) return {};

    if (permissionType === 'group' && selectedGroup && Array.isArray(groupPermsData)) {
      const grantedIds = new Set(
        groupPermsData
          .filter((p) => p.granted !== false && p.granted !== 0 && p.granted !== '0')
          .map((p) => Number(p.id))
          .filter((id) => Number.isFinite(id))
      );
      const perms = {};
      permissions.forEach((perm) => {
        const id = Number(perm.id);
        const granted = grantedIds.has(id);
        perms[id] = { ...perm, id, effective: granted, source: granted ? 'group' : 'none' };
      });
      return perms;
    }

    if (permissionType === 'role' && selectedRole && Array.isArray(rolePermsData)) {
      const grantedIds = new Set(
        rolePermsData.map((p) => Number(p.id)).filter((id) => Number.isFinite(id))
      );
      const perms = {};
      permissions.forEach((perm) => {
        const id = Number(perm.id);
        const granted = grantedIds.has(id);
        perms[id] = { ...perm, id, effective: granted, source: granted ? 'role' : 'none' };
      });
      return perms;
    }

    return {};
  };

  const effectivePermissions = getEffectivePermissions();

  const handlePermissionsChange = (changes) => {
    const changeMap =
      changes instanceof Map
        ? new Map(
            [...changes.entries()].map(([k, v]) => [Number(k), v]).filter(([k]) => Number.isFinite(k))
          )
        : new Map(
            Object.entries(changes || {})
              .map(([k, v]) => [Number(k), v])
              .filter(([k]) => Number.isFinite(k))
          );
    if (!changeMap.size) return;

    if (permissionType === 'group' && selectedGroup) {
      const currentPerms = Object.values(effectivePermissions).map((p) => {
        const id = Number(p.id);
        return {
          permission_id: id,
          granted: changeMap.has(id) ? Boolean(changeMap.get(id)) : p.effective === true,
        };
      });
      updateGroupPermissionsMutation.mutate({
        groupId: selectedGroup.id,
        permissions: currentPerms,
      });
    } else if (permissionType === 'role' && selectedRole) {
      const currentPerms = Object.values(effectivePermissions).map((p) => {
        const id = Number(p.id);
        return {
          permission_id: id,
          granted: changeMap.has(id) ? Boolean(changeMap.get(id)) : p.effective === true,
        };
      });
      updateRolePermissionsMutation.mutate({
        role: selectedRole,
        permissions: currentPerms,
      });
    }
  };

  const handlePermissionChange = (permissionId, granted) => {
    handlePermissionsChange({ [permissionId]: granted });
  };

  const setSectionPermissions = (perms, granted) => {
    const changes = {};
    for (const perm of perms) {
      changes[perm.id] = granted;
    }
    handlePermissionsChange(changes);
  };

  const clearSelection = (type) => {
    setPermissionType(type);
    setSelectedGroup(null);
    setSelectedRole('');
    setEditingAccessType(null);
  };

  const hasPermissionCatalog = permissions && permissions.length > 0;

  const editorActive =
    hasPermissionCatalog &&
    ((permissionType === 'group' && selectedGroup && !loadingGroupPerms && !groupPermsError) ||
      (permissionType === 'role' && selectedRole && !loadingRolePerms));

  const submitAccessType = (e) => {
    e.preventDefault();
    if (editingAccessType) {
      updateAccessTypeMutation.mutate({
        id: editingAccessType.id,
        name: accessTypeForm.name.trim().toLowerCase(),
        description: accessTypeForm.description.trim(),
        resource_type: accessTypeForm.resource_type.trim().toLowerCase(),
        action: accessTypeForm.action.trim().toLowerCase(),
      });
      return;
    }
    createAccessTypeMutation.mutate({
      name: accessTypeForm.name.trim().toLowerCase(),
      description: accessTypeForm.description.trim(),
      resource_type: accessTypeForm.resource_type.trim().toLowerCase(),
      action: accessTypeForm.action.trim().toLowerCase(),
      grant_admin: accessTypeForm.grant_admin !== false,
    });
  };

  const startEditAccessType = (perm) => {
    setEditingAccessType(perm);
    setAccessTypeForm({
      name: perm.name,
      description: perm.description || '',
      resource_type: perm.resource_type,
      action: perm.action,
      grant_admin: true,
    });
  };

  return (
    <div className="settings-section">
      <h2>
        <FiShield /> Access control
      </h2>
      <p className="permission-note" style={{ marginTop: 0 }}>
        Grant feature access through <strong>groups</strong>. Put users in a group under Settings → Groups. Role
        defaults are the baseline for login roles; access types define what can be granted.
      </p>

      <div className="permissions-container">
        <div className="permissions-selector">
          <div className="permission-type-selector">
            <button
              type="button"
              className={`btn ${permissionType === 'group' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => clearSelection('group')}
            >
              By group
            </button>
            <button
              type="button"
              className={`btn ${permissionType === 'role' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => clearSelection('role')}
            >
              Role defaults
            </button>
            <button
              type="button"
              className={`btn ${permissionType === 'catalog' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => clearSelection('catalog')}
            >
              Access types
            </button>
          </div>

          {permissionType === 'group' && (
            <div className="entity-selector">
              <label htmlFor="perm-select-group">Group</label>
              <select
                id="perm-select-group"
                value={selectedGroup?.id || ''}
                onChange={(e) => {
                  const groupId = parseInt(e.target.value, 10);
                  const g = groups?.find((x) => x.id === groupId);
                  setSelectedGroup(g || null);
                }}
              >
                <option value="">— Select a group —</option>
                {groups?.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {permissionType === 'role' && (
            <div className="entity-selector">
              <label htmlFor="perm-select-role">Role template</label>
              <select
                id="perm-select-role"
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
              >
                <option value="">— Select role —</option>
                {appRoles
                  .filter((r) => r.is_active)
                  .map((role) => (
                    <option key={role.slug} value={role.slug}>
                      {role.label}
                      {role.is_builtin ? '' : ` (${role.slug})`}
                    </option>
                  ))}
              </select>
            </div>
          )}
        </div>

        {permissionsQueryError ? (
          <div className="error">
            Could not load permission definitions:{' '}
            {permissionsQueryErr?.response?.data?.error ||
              permissionsQueryErr?.message ||
              'Request failed'}
          </div>
        ) : loadingPermissions ? (
          <div className="loading">Loading permissions...</div>
        ) : permissionType === 'catalog' ? (
          <div className="permissions-editor access-types-editor">
            <div className="permissions-info-header">
              <h3>Access types</h3>
              <p className="permission-note">
                Each row is a grantable capability. Create or edit types here, then assign them to groups (or role
                defaults). New types are granted to the admin role by default.
              </p>
            </div>

            <form className="access-type-form" onSubmit={submitAccessType}>
              <div className="access-type-form-grid">
                <label>
                  <span>Key (slug)</span>
                  <input
                    required
                    pattern="[a-z][a-z0-9_]{1,63}"
                    title="Lowercase slug, e.g. tab_jobs"
                    value={accessTypeForm.name}
                    onChange={(e) => setAccessTypeForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder="e.g. tab_reports"
                  />
                </label>
                <label>
                  <span>Resource</span>
                  <input
                    required
                    list="access-type-resources"
                    value={accessTypeForm.resource_type}
                    onChange={(e) =>
                      setAccessTypeForm((f) => ({ ...f, resource_type: e.target.value }))
                    }
                    placeholder="tab"
                  />
                  <datalist id="access-type-resources">
                    {PERMISSION_RESOURCE_ORDER.map((r) => (
                      <option key={r} value={r} />
                    ))}
                  </datalist>
                </label>
                <label>
                  <span>Action</span>
                  <input
                    required
                    list="access-type-actions"
                    value={accessTypeForm.action}
                    onChange={(e) => setAccessTypeForm((f) => ({ ...f, action: e.target.value }))}
                    placeholder="view"
                  />
                  <datalist id="access-type-actions">
                    {PERMISSION_ACTION_ORDER.map((a) => (
                      <option key={a} value={a} />
                    ))}
                  </datalist>
                </label>
                <label className="access-type-desc">
                  <span>Description</span>
                  <input
                    value={accessTypeForm.description}
                    onChange={(e) =>
                      setAccessTypeForm((f) => ({ ...f, description: e.target.value }))
                    }
                    placeholder="What this access allows"
                  />
                </label>
              </div>
              {!editingAccessType && (
                <label className="access-type-admin-grant">
                  <input
                    type="checkbox"
                    checked={accessTypeForm.grant_admin !== false}
                    onChange={(e) =>
                      setAccessTypeForm((f) => ({ ...f, grant_admin: e.target.checked }))
                    }
                  />
                  Also grant to admin role
                </label>
              )}
              <div className="access-type-form-actions">
                <LoadingButton
                  type="submit"
                  className="btn btn-primary"
                  loading={
                    createAccessTypeMutation.isLoading || updateAccessTypeMutation.isLoading
                  }
                >
                  {editingAccessType ? 'Save access type' : 'Add access type'}
                </LoadingButton>
                {editingAccessType && (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setEditingAccessType(null);
                      setAccessTypeForm(EMPTY_ACCESS_TYPE);
                    }}
                  >
                    Cancel
                  </button>
                )}
              </div>
            </form>

            {!hasPermissionCatalog ? (
              <div className="permissions-seed-missing">
                <p>
                  <strong>No access types yet.</strong> Add one above, or seed the built-in catalog from SQL if this
                  is a fresh database.
                </p>
              </div>
            ) : (
              <div className="access-types-list permission-sections">
                {sortedResourceEntries.map(([resourceType, perms]) => (
                  <section key={resourceType} className="permission-section">
                    <header className="permission-section-header">
                      <div className="permission-section-heading">
                        <h4 className="permission-section-title">
                          {PERMISSION_RESOURCE_LABELS[resourceType] ||
                            `${resourceType.charAt(0).toUpperCase() + resourceType.slice(1)}`}
                        </h4>
                        <span className="permission-section-count">{perms.length} types</span>
                      </div>
                    </header>
                    <ul className="permission-checkbox-list access-types-checklist">
                      {perms.map((perm) => (
                        <li key={perm.id} className="permission-checkbox-row access-type-manage-row">
                          <div className="permission-checkbox-body">
                            <span className="permission-checkbox-title">
                              {perm.description || perm.name}
                            </span>
                            <span className="permission-checkbox-meta">
                              <code className="permission-key">{perm.name}</code>
                              <span className="action-badge action-badge--compact">
                                {ACTION_DISPLAY[perm.action] || perm.action}
                              </span>
                            </span>
                          </div>
                          <div className="access-type-row-actions">
                            <button
                              type="button"
                              className="btn btn-secondary btn-sm"
                              onClick={() => startEditAccessType(perm)}
                            >
                              <FiEdit2 /> Edit
                            </button>
                            <button
                              type="button"
                              className="btn btn-danger btn-sm"
                              disabled={perm.name === 'permission_manage'}
                              onClick={async () => {
                                if (
                                  await appConfirm(
                                    `Delete access type "${perm.name}"? This removes it from all groups and roles.`
                                  )
                                ) {
                                  deleteAccessTypeMutation.mutate(perm.id);
                                }
                              }}
                            >
                              <FiTrash2 /> Delete
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </section>
                ))}
              </div>
            )}
          </div>
        ) : !hasPermissionCatalog ? (
          <div className="permissions-seed-missing">
            <p>
              <strong>No permission definitions in the database.</strong> Add them under{' '}
              <button type="button" className="link-btn" onClick={() => clearSelection('catalog')}>
                Access types
              </button>
              , or run the seed migration for a fresh D1 database.
            </p>
            <pre className="permissions-cli-hint">
              npx wrangler d1 execute YOUR_DB_NAME --remote --file=./database/migrations/seed_permissions_d1.sql
            </pre>
          </div>
        ) : permissionType === 'group' && selectedGroup && loadingGroupPerms ? (
          <div className="loading">Loading group permissions...</div>
        ) : permissionType === 'group' && selectedGroup && groupPermsError ? (
          <div className="error">Could not load permissions for this group.</div>
        ) : permissionType === 'role' && selectedRole && loadingRolePerms ? (
          <div className="loading">Loading role permissions...</div>
        ) : editorActive ? (
          <div className="permissions-editor">
            <div className="permissions-info-header">
              <h3>
                {permissionType === 'group' && `Group: ${selectedGroup.name}`}
                {permissionType === 'role' && `Role defaults: ${roleLabel(selectedRole, appRoles)}`}
              </h3>
              {permissionType === 'group' && (
                <p className="permission-note">
                  Checked items are <strong>granted to every member</strong> of this group (on top of
                  their role defaults). Unchecked leaves the role baseline unchanged. For sidebar
                  navigation, grant <code>tab_*</code> access types (e.g. <code>tab_dashboard</code>,{' '}
                  <code>tab_jobs</code>) — feature keys alone do not show menu items. Add people under
                  Settings → Groups, then have them refresh the app.
                </p>
              )}
              {permissionType === 'role' && (
                <p className="permission-note role-defaults-warning">
                  Baseline for all accounts with the <strong>{selectedRole}</strong> role. Prefer
                  groups for day-to-day access changes.
                </p>
              )}
            </div>

            <p className="permissions-section-hint">
              Permissions are grouped by area. Use each section header to select or clear all
              checkboxes in that group. <strong>Tabs</strong> control which areas appear; other
              rows control create / edit / delete where the API enforces them.
            </p>

            <div className="permission-sections">
              {sortedResourceEntries.map(([resourceType, perms]) => {
                const grantedCount = perms.filter(
                  (perm) => effectivePermissions[perm.id]?.effective === true
                ).length;
                const allGranted = grantedCount === perms.length && perms.length > 0;
                const someGranted = grantedCount > 0 && !allGranted;
                const sectionId = `perm-section-${resourceType}`;
                const isBusy =
                  updateGroupPermissionsMutation.isLoading ||
                  updateRolePermissionsMutation.isLoading;

                return (
                  <section
                    key={resourceType}
                    className="permission-section"
                    aria-labelledby={sectionId}
                  >
                    <header className="permission-section-header">
                      <div className="permission-section-heading">
                        <label className="permission-section-master">
                          <input
                            type="checkbox"
                            className="permission-checkbox"
                            checked={allGranted}
                            ref={(el) => {
                              if (el) el.indeterminate = someGranted;
                            }}
                            disabled={isBusy || !perms.length}
                            onChange={(e) => setSectionPermissions(perms, e.target.checked)}
                            aria-label={
                              allGranted
                                ? `Clear all in ${PERMISSION_RESOURCE_LABELS[resourceType] || resourceType}`
                                : `Grant all in ${PERMISSION_RESOURCE_LABELS[resourceType] || resourceType}`
                            }
                          />
                          <span id={sectionId} className="permission-section-title">
                            {PERMISSION_RESOURCE_LABELS[resourceType] ||
                              `${resourceType.charAt(0).toUpperCase()}${resourceType.slice(1)}`}
                          </span>
                        </label>
                        <span className="permission-section-count">
                          {grantedCount}/{perms.length} granted
                        </span>
                      </div>
                      <div className="permission-section-actions">
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          disabled={isBusy || allGranted}
                          onClick={() => setSectionPermissions(perms, true)}
                        >
                          Select all
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          disabled={isBusy || grantedCount === 0}
                          onClick={() => setSectionPermissions(perms, false)}
                        >
                          Clear
                        </button>
                      </div>
                    </header>

                    <ul className="permission-checkbox-list">
                      {perms.map((perm) => {
                        const effective = effectivePermissions[perm.id];
                        const isGranted = effective?.effective === true;
                        const source = effective?.source;
                        const checkboxId = `perm-cb-${perm.id}`;
                        return (
                          <li key={perm.id} className="permission-checkbox-row">
                            <label htmlFor={checkboxId} className="permission-checkbox-label">
                              <input
                                id={checkboxId}
                                type="checkbox"
                                className="permission-checkbox"
                                checked={isGranted}
                                disabled={isBusy}
                                onChange={(e) =>
                                  handlePermissionChange(perm.id, e.target.checked)
                                }
                              />
                              <span className="permission-checkbox-body">
                                <span className="permission-checkbox-title">
                                  {perm.description || perm.name}
                                </span>
                                <span className="permission-checkbox-meta">
                                  <code className="permission-key">{perm.name}</code>
                                  <span className="action-badge action-badge--compact">
                                    {ACTION_DISPLAY[perm.action] || perm.action}
                                  </span>
                                  {source === 'role' && (
                                    <span className="permission-source-badge">Role</span>
                                  )}
                                  {source === 'group' && (
                                    <span className="permission-source-badge group">Group</span>
                                  )}
                                </span>
                              </span>
                            </label>
                          </li>
                        );
                      })}
                    </ul>
                  </section>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="permissions-placeholder">
            <p>
              {permissionType === 'group' &&
                'Select a group to grant access types to its members. Create groups under Settings → Groups.'}
              {permissionType === 'role' &&
                'Select a role to edit its default permissions, or add roles under Settings → Roles.'}
            </p>
          </div>
        )}
      </div>

      <div className="permissions-help">
        <h3>How access is resolved</h3>
        <div className="permission-help-content">
          <div className="help-section">
            <h4>Priority order</h4>
            <ul>
              <li>
                <strong>Group grants</strong> — primary way to give access (union across a user’s groups)
              </li>
              <li>
                <strong>Role defaults</strong> — baseline for the user’s login role
              </li>
            </ul>
          </div>
          <div className="help-section">
            <h4>Admin workflow</h4>
            <ul>
              <li>
                <strong>Access types</strong> — define what can be granted
              </li>
              <li>
                <strong>By group</strong> — turn access types on for a group
              </li>
              <li>
                <strong>Groups</strong> — add users to groups (not per-user permission matrices)
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
